/** Ordered description body: typed text lines interleaved with referenced activity chips. */

import { stripReportTextHtml } from './reportTextHtml';

export type ReportTextBlock = {
  id: string,
  kind: 'text',
  text: string,
};

export type ReportLogBlock = {
  id: string,
  kind: 'log',
  sourceId: string,
  tmpId: number,
  message: string,
  type: string,
  date?: string,
  value?: string,
  units?: string,
  /** Frozen /logs Value-header when this chip was inserted. */
  valueMode?: 'value' | 'units',
  /** Merged frozen source ids when inserted from a combined SKU row. */
  combinedSourceIds?: string[],
  /** Combined N · unique activity types — combined preview/PDF only. */
  combinedDetails?: string,
  /** Actual log type / SKU when `type` is a department or inventory-group label. */
  activityLogType?: string,
};

/** Activity / staff report body — image may be local until send, then GCS id. */
export type ReportPictureBlock = {
  id: string,
  kind: 'picture',
  /** GCS images bucket id from addphotos (empty while still local). */
  imageId: string,
  fileName: string,
  /** Data URI kept in the draft until the report is sent — never sent to the server raw. */
  localDataUri?: string,
};

export type ReportDescriptionBlock = ReportTextBlock | ReportLogBlock | ReportPictureBlock;

/** Max pictures on one report body (Command Center + Activity reports). */
export const REPORT_PICTURE_MAX = 10;

export const countDescriptionPictures = (blocks: ReportDescriptionBlock[]): number => (
  blocks.filter((b) => b.kind === 'picture').length
);

export const pictureBlocksFromDescription = (
  blocks: ReportDescriptionBlock[],
): ReportPictureBlock[] => blocks.filter((b): b is ReportPictureBlock => b.kind === 'picture');

export const newReportBlockId = () => (
  `rb_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
);

export const createEmptyTextBlock = (text = ''): ReportTextBlock => ({
  id: newReportBlockId(),
  kind: 'text',
  text,
});

export const createPictureBlock = (
  imageId: string,
  fileName: string,
  localDataUri?: string,
): ReportPictureBlock => ({
  id: newReportBlockId(),
  kind: 'picture',
  imageId: String(imageId || '').trim(),
  fileName: String(fileName || 'image').trim() || 'image',
  ...(localDataUri ? { localDataUri } : {}),
});

/** Draft-only picture — upload to GCS happens on send, not when the user adds it. */
export const createLocalPictureBlock = (
  localDataUri: string,
  fileName: string,
): ReportPictureBlock => (
  createPictureBlock('', fileName, localDataUri)
);

/** Preview / editor thumb — prefer local draft bytes, else GCS. */
export const reportPictureDisplaySrc = (pic: {
  imageId?: string,
  localDataUri?: string,
}): string => {
  const local = String(pic.localDataUri || '').trim();
  if (local.startsWith('data:')) return local;
  const id = String(pic.imageId || '').trim();
  if (!id) return '';
  return `https://storage.googleapis.com/images_wiegfbiwudjdvh/${id}`;
};

export const createInitialDescriptionBlocks = (): ReportDescriptionBlock[] => (
  [createEmptyTextBlock()]
);

/** Typed text only — used for the description char budget. */
export const sumDescriptionTextChars = (blocks: ReportDescriptionBlock[]): number => (
  // Count visible characters only — HTML tags must not eat the budget.
  blocks.reduce((n, b) => (
    b.kind === 'text' ? n + stripReportTextHtml(b.text).length : n
  ), 0)
);

export const plainDescriptionFromBlocks = (blocks: ReportDescriptionBlock[]): string => (
  blocks
    .filter((b): b is ReportTextBlock => b.kind === 'text')
    .map((b) => stripReportTextHtml(b.text))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
);

export const referencedSourceIdsFromBlocks = (
  blocks: ReportDescriptionBlock[],
): Set<string> => {
  const ids = new Set<string>();
  blocks.forEach((b) => {
    if (b.kind !== 'log') return;
    if (b.combinedSourceIds?.length) {
      b.combinedSourceIds.forEach((id) => ids.add(id));
      return;
    }
    ids.add(b.sourceId);
  });
  return ids;
};

export const logBlocksFromDescription = (
  blocks: ReportDescriptionBlock[],
): ReportLogBlock[] => blocks.filter((b): b is ReportLogBlock => b.kind === 'log');

/** Activity-set s/n (#1, #2, …) — grouping key, never the log UUID. */
export const activityLogSerial = (log: { tmpId?: number }): number => {
  const n = Math.floor(Number(log.tmpId));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Sort by activity s/n, then split where numbers are not consecutive.
 * Selecting 1,2,3,8,9,16 → [1,2,3] [8,9] [16].
 */
export const splitLogsByConsecutiveActivitySerial = <T extends { tmpId?: number }>(
  logs: T[],
): T[][] => {
  if (!logs.length) return [];
  const sorted = [...logs].sort((a, b) => activityLogSerial(a) - activityLogSerial(b));
  const runs: T[][] = [];
  sorted.forEach((log) => {
    const serial = activityLogSerial(log);
    const prev = runs[runs.length - 1];
    if (prev && serial === activityLogSerial(prev[prev.length - 1]) + 1) {
      prev.push(log);
      return;
    }
    runs.push([log]);
  });
  return runs;
};

/** Merge only adjacent text in a gap; keep pictures in place between texts. */
const joinGapNonLogBlocks = (parts: ReportDescriptionBlock[]): ReportDescriptionBlock[] => {
  if (!parts.length) return [createEmptyTextBlock()];
  const out: ReportDescriptionBlock[] = [];
  parts.forEach((block) => {
    const prev = out[out.length - 1];
    if (block.kind === 'text' && prev?.kind === 'text') {
      const joiner = prev.text && block.text
        && !prev.text.endsWith('\n')
        && !block.text.startsWith('\n')
        ? '\n'
        : '';
      out[out.length - 1] = { ...prev, text: `${prev.text}${joiner}${block.text}` };
      return;
    }
    out.push(block);
  });
  return out.length ? out : [createEmptyTextBlock()];
};

/**
 * Place consecutive s/n chips together and keep text/picture slots between each
 * run so the body can type between pairs (1–3, then 8–9, then 16).
 * Pictures stay in their gap (not dropped when logs rearrange).
 */
export const layoutDescriptionBlocksByConsecutiveActivitySerial = (
  blocks: ReportDescriptionBlock[],
): ReportDescriptionBlock[] => {
  const logs = logBlocksFromDescription(blocks);
  if (!logs.length) return blocks;

  const runs = splitLogsByConsecutiveActivitySerial(logs);
  const gapBlocks: ReportDescriptionBlock[][] = Array.from(
    { length: runs.length + 1 },
    () => [],
  );
  const runIndexByLogId = new Map<string, number>();
  runs.forEach((run, i) => {
    run.forEach((log) => runIndexByLogId.set(log.id, i));
  });

  let lastRunIndex = -1;
  blocks.forEach((block) => {
    if (block.kind === 'log') {
      const idx = runIndexByLogId.get(block.id);
      if (idx != null) lastRunIndex = idx;
      return;
    }
    // text + picture stay between log runs
    gapBlocks[lastRunIndex + 1].push(block);
  });

  const out: ReportDescriptionBlock[] = [];
  runs.forEach((run, i) => {
    out.push(...joinGapNonLogBlocks(gapBlocks[i]));
    run.forEach((log) => out.push(log));
  });
  out.push(...joinGapNonLogBlocks(gapBlocks[runs.length]));
  return out;
};

/** Merge adjacent text, group consecutive activity s/n, keep a trailing text line. */
export const normalizeDescriptionBlocks = (
  blocks: ReportDescriptionBlock[],
): ReportDescriptionBlock[] => {
  const laidOut = layoutDescriptionBlocksByConsecutiveActivitySerial(blocks);
  const next: ReportDescriptionBlock[] = [];
  laidOut.forEach((block) => {
    const prev = next[next.length - 1];
    if (block.kind === 'text' && prev?.kind === 'text') {
      // Join with a newline so two paragraphs stay readable.
      const joiner = prev.text && block.text
        ? (prev.text.endsWith('\n') || block.text.startsWith('\n') ? '' : '\n')
        : '';
      next[next.length - 1] = {
        ...prev,
        text: `${prev.text}${joiner}${block.text}`,
      };
      return;
    }
    next.push(block);
  });
  if (!next.length) return [createEmptyTextBlock()];
  // Cap pictures after layout so inserts cannot sneak past REPORT_PICTURE_MAX.
  let pictureCount = 0;
  const capped = next.filter((block) => {
    if (block.kind !== 'picture') return true;
    pictureCount += 1;
    return pictureCount <= REPORT_PICTURE_MAX;
  });
  if (capped[capped.length - 1].kind === 'log' || capped[capped.length - 1].kind === 'picture') {
    capped.push(createEmptyTextBlock());
  }
  if (capped[0].kind === 'log' || capped[0].kind === 'picture') {
    capped.unshift(createEmptyTextBlock());
  }
  return capped;
};

export type DescriptionBlockPayload =
  | { kind: 'text', text: string }
  | {
    kind: 'log',
    sourceId?: string,
    tmpId?: number,
    message: string,
    type?: string,
    date?: string,
    value?: string,
    units?: string,
    valueMode?: 'value' | 'units',
    /** Frozen ids when this chip was a Combine SKUs row. */
    combinedSourceIds?: string[],
    combinedDetails?: string,
    activityLogType?: string,
  }
  | { kind: 'picture', imageId: string, fileName: string, localDataUri?: string };

export const toDescriptionBlockPayload = (
  blocks: ReportDescriptionBlock[],
): DescriptionBlockPayload[] => {
  const out: DescriptionBlockPayload[] = [];
  blocks.forEach((b) => {
    if (b.kind === 'text') {
      out.push({ kind: 'text', text: b.text });
      return;
    }
    if (b.kind === 'picture') {
      // Only uploaded GCS ids go on the wire — local drafts upload on send first.
      const imageId = String(b.imageId || '').trim();
      if (!imageId) return;
      out.push({
        kind: 'picture',
        imageId,
        fileName: b.fileName,
      });
      return;
    }
    out.push({
      kind: 'log',
      sourceId: b.sourceId,
      tmpId: b.tmpId,
      message: b.message,
      type: b.type,
      date: b.date,
      value: b.value,
      units: b.units,
      ...(b.valueMode === 'units' || b.valueMode === 'value'
        ? { valueMode: b.valueMode }
        : {}),
      ...(b.combinedSourceIds?.length
        ? { combinedSourceIds: [...b.combinedSourceIds] }
        : {}),
      ...(b.combinedDetails ? { combinedDetails: b.combinedDetails } : {}),
      ...(b.activityLogType ? { activityLogType: b.activityLogType } : {}),
    });
  });
  // Drop trailing empty text for the wire payload.
  if (
    out.length > 1
    && out[out.length - 1].kind === 'text'
    && !String((out[out.length - 1] as { text?: string }).text || '').trim()
  ) {
    out.pop();
  }
  return out;
};

/** Rebuild editor blocks from a stored report payload (mirror / copy to new). */
export const descriptionBlocksFromPayload = (
  raw: unknown,
): ReportDescriptionBlock[] => {
  if (!Array.isArray(raw) || !raw.length) return createInitialDescriptionBlocks();
  const out: ReportDescriptionBlock[] = [];
  raw.forEach((item, i) => {
    if (!item || typeof item !== 'object') return;
    const row = item as DescriptionBlockPayload;
    if (row.kind === 'picture') {
      const imageId = String(row.imageId || '').trim();
      const localDataUri = String(
        (row as { localDataUri?: string }).localDataUri || '',
      ).trim();
      if (imageId || localDataUri.startsWith('data:')) {
        out.push(createPictureBlock(
          imageId,
          String(row.fileName || 'image'),
          localDataUri.startsWith('data:') ? localDataUri : undefined,
        ));
      }
      return;
    }
    if (row.kind === 'log') {
      out.push({
        id: newReportBlockId(),
        kind: 'log',
        sourceId: String(row.sourceId || ''),
        tmpId: Number(row.tmpId) > 0 ? Math.floor(Number(row.tmpId)) : i + 1,
        message: String(row.message || ''),
        type: String(row.type || 'Activity'),
        date: row.date ? String(row.date) : undefined,
        value: row.value != null ? String(row.value) : undefined,
        units: row.units != null ? String(row.units) : undefined,
        valueMode: row.valueMode === 'units' ? 'units' : undefined,
        combinedSourceIds: Array.isArray(row.combinedSourceIds)
          ? row.combinedSourceIds.map((id) => String(id || '').trim()).filter(Boolean)
          : undefined,
        combinedDetails: row.combinedDetails ? String(row.combinedDetails) : undefined,
        activityLogType: row.activityLogType ? String(row.activityLogType) : undefined,
      });
      return;
    }
    if (row.kind === 'text') {
      out.push({
        id: newReportBlockId(),
        kind: 'text',
        text: String(row.text || ''),
      });
    }
  });
  return normalizeDescriptionBlocks(out);
};
